/**
 * Frontend service for Google Local Services Ads (GLSA) leads.
 *
 * fetchLeadsFromApi() — calls syncGlsaLeads Cloud Function which pulls leads
 * directly from the Google GLSA REST API and returns them in the response.
 * No Firestore collection is required on the client side.
 */

import { getFunctions, httpsCallable } from 'firebase/functions';

// ─── Types ────────────────────────────────────────────────────────────────────

/** Lead shape returned from the API (dates are ISO strings). */
export interface GlsaLeadDoc {
  id: string;
  accountId: string;
  leadType: string;         // 'PHONE_CALL' | 'MESSAGE'
  status: string;           // 'NEW' | 'ACTIVE' | 'BOOKED' | 'DECLINED' | 'EXPIRED' | 'DISABLED'
  contactName: string;
  contactPhone: string;
  jobType: string;
  createdAt: string | null; // ISO date string from API
  updatedAt: string | null;
  note: string;
  chargeStatus: string;     // 'CHARGED' | 'NOT_CHARGED' | 'CREDIT'
  disputeStatus: string | null;
  timezone: string;
  leadPrice: number;
  currency: string;
  leadCategory: string;
  conversationWithGoogleGuarantee: boolean;
}

interface SyncLeadsResponse {
  synced: number;
  hasMore: boolean;
  leads: GlsaLeadDoc[];
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Fetch leads directly from the Google GLSA API via Cloud Function.
 * Returns the lead objects in the response — no Firestore read needed.
 *
 * @param pageSize  How many leads to fetch (max 100, default 50).
 */
export async function fetchLeadsFromApi(pageSize = 50): Promise<GlsaLeadDoc[]> {
  const fns = getFunctions();
  const callable = httpsCallable<{ pageSize: number }, SyncLeadsResponse>(fns, 'syncGlsaLeads');
  const result = await callable({ pageSize });
  return result.data.leads ?? [];
}

/** @deprecated Use fetchLeadsFromApi instead. */
export async function syncLeadsFromApi(pageSize = 50): Promise<{ synced: number; hasMore: boolean }> {
  const fns = getFunctions();
  const callable = httpsCallable<{ pageSize: number }, SyncLeadsResponse>(fns, 'syncGlsaLeads');
  const result = await callable({ pageSize });
  return { synced: result.data.synced, hasMore: result.data.hasMore };
}
